import React, { useState } from 'react';
import { sendAdminNotification } from './notificationService';

export default function GetInTouchSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (!/^[a-zA-Z\s'-]+$/.test(formData.name)) {
      newErrors.name = 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (formData.email.length > 100) {
      newErrors.email = 'Email must be less than 100 characters';
    }
    
    // Phone validation (optional but if provided, must be valid)
    if (formData.phone.trim()) {
      // Remove all non-digit characters except leading +
      const phoneDigits = formData.phone.replace(/[^\d+]/g, '');
      
      // Check for +91 (India) and 10 digits
      if (phoneDigits.startsWith('+91')) {
        if (!/^\+91\d{10}$/.test(phoneDigits)) {
          newErrors.phone = 'Phone number must be 10 digits after +91 country code';
        }
      } else {
        // Check for 10 digits and valid Indian mobile (starts with 6-9)
        if (!/^[6-9]\d{9}$/.test(phoneDigits)) {
          newErrors.phone = 'Please enter a valid 10-digit Indian phone number (starting with 6-9)';
        }
      }
    }
    
    // Subject validation (optional)
    if (formData.subject.trim() && formData.subject.length < 3) {
      newErrors.subject = 'Subject must be at least 3 characters';
    }
    
    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Please provide at least 10 characters in your message';
    } else if (formData.message.length > 1000) {
      newErrors.message = 'Message must be less than 1000 characters';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Parse the full name into firstName and lastName
    const nameParts = formData.name.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    // Create submission object for server
    const submissionData = {
      firstName: firstName,
      lastName: lastName,
      email: formData.email,
      phone: formData.phone,
      service: formData.subject,
      message: formData.message,
      source: "home_page"
    };
    
    try {
      // Send to server
      const response = await fetch('/api/submissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Also save to localStorage as backup
        const existingSubmissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
        existingSubmissions.push(result.submission);
        localStorage.setItem('contactSubmissions', JSON.stringify(existingSubmissions));
        
        // Send notification to admin
        sendAdminNotification(result.submission);
        
        // Show success message
        setShowSuccess(true);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setErrors({});
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      } else {
        const errorData = await response.json();
        setErrors({ submit: errorData.error || 'Failed to submit form' });
      }
    } catch (error) {
      console.error('Submission error:', error);
      // Fallback to localStorage if server is unavailable
      const existingSubmissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
      const newSubmission = {
        id: Date.now(),
        firstName: firstName,
        lastName: lastName,
        email: formData.email,
        phone: formData.phone,
        service: formData.subject,
        message: formData.message,
        submissionDate: new Date().toISOString(),
        source: "home_page"
      };
      existingSubmissions.push(newSubmission);
      localStorage.setItem('contactSubmissions', JSON.stringify(existingSubmissions));
      
      // Send notification
      sendAdminNotification(newSubmission);
      
      // Show success anyway (saved to localStorage)
      setShowSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setErrors({});
      
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    }
  };

  return (
    <section id="get-in-touch" className="relative bg-gray-50 py-24 lg:py-32 overflow-hidden border-b border-gray-200">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[150px] pointer-events-none z-0"></div>
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[150px] pointer-events-none z-0"></div>
      <div className="absolute inset-0 bg-blueprint opacity-30 z-0 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center mb-16 lg:mb-20">
          <div className="inline-flex items-center border border-gray-200 px-4 py-2 bg-white/80 backdrop-blur-sm text-xs font-bold uppercase tracking-widest text-amber-600 mb-6 rounded-md shadow-sm">
            <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
            Get In Touch
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight uppercase text-gray-900 mb-4">
            Let's Start Your <br />
            <span className="text-amber-600">Journey</span>
          </h2>
          <p className="text-gray-600 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            Ready to transform your digital landscape with AI-driven solutions? Connect with our engineering architects and innovation experts who turn your challenges into competitive advantages.
          </p>
        </div>

        {/* Main Grid for Contact Card & Map */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-stretch">
        
          {/* Left: Contact Information Card */}
          <div className="bg-white p-8 lg:p-10 rounded-[2rem] shadow-2xl border border-gray-100 h-full flex flex-col relative overflow-hidden group">
            {/* Subtle background accent */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-50 rounded-bl-[100px] -z-0 transition-colors duration-500 group-hover:bg-amber-100/50"></div>
            
            <div className="mb-8 relative z-10">
              <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-3 tracking-tight">
                Start Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-amber-600">Digital Flight</span>
              </h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                Share your innovation goals or technical challenge with us. Our team of AI and cloud experts will respond within 24 hours with strategic insights and solutions tailored to your enterprise needs.
              </p>
            </div>
            
            {!showSuccess ? (
              <form onSubmit={handleSubmit} className="space-y-5 flex-grow flex flex-col relative z-10">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="text-[11px] font-bold text-gray-500 uppercase tracking-widest block mb-2">Full Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      autoComplete="name"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:bg-white focus:ring-2 focus:border-amber-500 transition-all text-sm outline-none placeholder-gray-400 ${
                        errors.name
                          ? 'border-red-500 focus:ring-red-500/40'
                          : 'border-gray-200 focus:ring-amber-500/40'
                      }`}
                      required
                    />
                    {errors.name && <p className="text-red-500 text-xs font-bold mt-1.5">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="text-[11px] font-bold text-gray-500 uppercase tracking-widest block mb-2">Email Address <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      autoComplete="email"
                      placeholder="youremailaddress@gmail.com"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:bg-white focus:ring-2 focus:border-amber-500 transition-all text-sm outline-none placeholder-gray-400 ${
                        errors.email
                          ? 'border-red-500 focus:ring-red-500/40'
                          : 'border-gray-200 focus:ring-amber-500/40'
                      }`}
                      required
                    />
                    {errors.email && <p className="text-red-500 text-xs font-bold mt-1.5">{errors.email}</p>}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="phone" className="text-[11px] font-bold text-gray-500 uppercase tracking-widest block mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      placeholder="+91 75465 43210"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:bg-white focus:ring-2 focus:border-amber-500 transition-all text-sm outline-none placeholder-gray-400 ${
                        errors.phone
                          ? 'border-red-500 focus:ring-red-500/40'
                          : 'border-gray-200 focus:ring-amber-500/40'
                      }`}
                    />
                    {errors.phone && <p className="text-red-500 text-xs font-bold mt-1.5">{errors.phone}</p>}
                  </div>
                  <div>
                    <label htmlFor="subject" className="text-[11px] font-bold text-gray-500 uppercase tracking-widest block mb-2">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      id="subject"
                      placeholder="How can we help you?"
                      value={formData.subject}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:bg-white focus:ring-2 focus:border-amber-500 transition-all text-sm outline-none placeholder-gray-400 ${
                        errors.subject
                          ? 'border-red-500 focus:ring-red-500/40'
                          : 'border-gray-200 focus:ring-amber-500/40'
                      }`}
                    />
                    {errors.subject && <p className="text-red-500 text-xs font-bold mt-1.5">{errors.subject}</p>}
                  </div>
                </div>
                <div className="flex-grow flex flex-col">
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="message" className="text-[11px] font-bold text-gray-500 uppercase tracking-widest block">Message <span className="text-red-500">*</span></label>
                    <span className="text-xs text-gray-400">{formData.message.length}/1000</span>
                  </div>
                  <textarea
                    name="message"
                    id="message"
                    rows="3"
                    placeholder="Tell us briefly about your project..."
                    value={formData.message}
                    onChange={handleChange}
                    maxLength="1000"
                    className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:bg-white focus:ring-2 focus:border-amber-500 transition-all text-sm outline-none resize-none flex-grow placeholder-gray-400 ${
                      errors.message
                        ? 'border-red-500 focus:ring-red-500/40'
                        : 'border-gray-200 focus:ring-amber-500/40'
                    }`}
                    required
                  ></textarea>
                  {errors.message && <p className="text-red-500 text-xs font-bold mt-1.5">{errors.message}</p>}
                </div>
                <div className="pt-2">
                  <button
                    type="submit"
                    className="w-full flex justify-center items-center gap-3 py-3.5 px-4 border border-transparent rounded-xl shadow-lg text-sm font-bold uppercase tracking-widest text-white bg-gray-900 hover:bg-black hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group/btn"
                  >
                    <span>Send Message</span>
                    <svg className="w-4 h-4 text-amber-500 transform group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                    </svg>
                  </button>
                </div>
              </form>
            ) : (
              <div className="relative z-10 text-center bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 p-10 rounded-2xl shadow-lg">
                <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3 className="text-2xl font-extrabold text-gray-900">Message Received!</h3>
                <p className="text-gray-700 mt-3 text-lg">Thank you for reaching out to Garud Stacks.</p>
                <p className="text-gray-600 mt-1 text-sm">Our innovation team will review your inquiry and connect with you within 24 hours.</p>
              </div>
            )}
          </div>
        
          {/* Right: Premium Office Image & Contact Info */}
          <div className="relative w-full h-full min-h-[450px] bg-white p-3 rounded-[2.5rem] shadow-xl border border-gray-200 group hover:border-amber-300 transition-colors duration-500">
            <div className="w-full h-full rounded-[2rem] overflow-hidden relative">
              {/* Call Center Agent Image */}
              <img
                src="https://images.unsplash.com/photo-1556742044-3c52d6e88c62?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                alt="Call Center Agent"
                className="w-full h-full object-cover transform transition-transform duration-1000 group-hover:scale-105"
              />
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
              
              {/* Floating Content */}
              <div className="absolute inset-0 p-6 sm:p-8 flex flex-col justify-between">
                {/* Top Badge */}
                <div className="self-end bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-xl text-white flex items-center gap-2 shadow-lg">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  <span className="text-[10px] font-bold uppercase tracking-widest">Available 24/7</span>
                </div>

                {/* Bottom Info Box */}
                <div className="transform transition-transform duration-500 group-hover:-translate-y-2">
                  <h4 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight mb-3">
                    Innovation <br />
                    <span className="text-amber-500">Hub 24/7</span>
                  </h4>
                  <p className="text-gray-300 text-sm leading-relaxed max-w-sm mb-6">
                    Our AI architects and innovation team are here round-the-clock to elevate your business with cutting-edge cloud and AI solutions.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-5 pt-5 border-t border-white/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-500 flex-shrink-0">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                        </svg>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Direct Line</p>
                        <a href="tel:+919522950225" className="text-white font-medium text-sm hover:text-amber-500 transition-colors">+91 95229 50225</a><br />
                        <a href="tel:+919806520216" className="text-white font-medium text-sm hover:text-amber-500 transition-colors">+91 98065 20216</a>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-500 flex-shrink-0">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLineCap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                        </svg>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Email Support</p>
                        <a href="mailto:garudstacks@gmail.com" className="text-white font-medium text-sm hover:text-amber-500 transition-colors">garudstacks@gmail.com</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
      </div>
    </section>
  );
}
